// Unified product/variant field composition helpers with a LIGHT vs FULL strategy.
// LIGHT is used for list/search/grid contexts to keep payloads small.
// FULL is used on product detail pages or variant-heavy contexts.

// Superset variant fields (union of list + detail needs)
const VARIANT_SUPERSET_FIELDS = `
  id
  title
  availableForSale
  quantityAvailable
  price { amount currencyCode }
  compareAtPrice { amount currencyCode }
  image { altText url }
  torsoOverlayValue: metafield(namespace: "custom", key: "torso_overlay") { value }
  skintoneValue: metafield(namespace: "custom", key: "skin_tone") { value }
  reverseImage: metafield(namespace: "custom", key: "reverse_image") { reference { ... on MediaImage { image { url } } } }
`;

// Base product fields builder
export const productBaseFields = ({
    warningKey = 'product_warning',
    includeImages = true,
    imageCount = 1,
    includeCollections = 50,
    collectionsCount = 10,
    includeTags = false,
} = {}) => `
  id
  title
  handle
  availableForSale
  descriptionHtml
  totalInventory
  priceRange { maxVariantPrice { amount currencyCode } minVariantPrice { amount currencyCode } }
  compareAtPriceRange { maxVariantPrice { amount currencyCode } minVariantPrice { amount currencyCode } }
  featuredImage { altText url }
  ${includeImages ? `images(first: ${imageCount}) { edges { node { url altText } } }` : ''}
  ${includeCollections ? `collections(first: ${collectionsCount}) { edges { node { id title handle } } }` : ''}
  ${includeTags ? 'tags' : ''}
  productWarningValue: metafield(namespace: "custom", key: "${warningKey}") { value }
  doubleSidedHeadTextLabels: metafield(namespace: "custom", key: "double_sided_heads_naming") { value }
`;

// Helper to wrap variants (supports nodes or edges form)
export const variantsAll = ({ shape = 'nodes', first = 50, fields = VARIANT_SUPERSET_FIELDS } = {}) =>
    shape === 'edges'
        ? `variants(first: ${first}) { edges { node { ${fields} } } }`
        : `variants(first: ${first}) { nodes { ${fields} } }`;

// FULL product field set (detail pages)
export const FULL_PRODUCT_FIELDS = ({
    warningKey = 'product_warning',
    variantShape = 'nodes',
    variantFirst = 50,
    imageCount = 200,
} = {}) => `
  ${productBaseFields({
      warningKey,
      includeImages: true,
      imageCount,
      includeCollections: true,
      collectionsCount: 50,
      includeTags: true,
  })}
  ${variantsAll({ shape: variantShape, first: variantFirst })}
`;

// LIGHT product list fields (collection/search grids)
export const LIGHT_PRODUCT_LIST_FIELDS = ({
    warningKey = 'product_warning',
    variantShape = 'nodes',
    variantFirst = 5,
    imageCount = 1,
    includeCollections = true,
    includeTags = false,
} = {}) => `
  ${productBaseFields({
      warningKey,
      includeImages: true,
      imageCount,
      includeCollections,
      collectionsCount: includeCollections ? 50 : 25,
      includeTags,
  })}
  ${variantsAll({
      shape: variantShape,
      first: variantFirst,
      fields: `
      id
      title
      availableForSale
      price { amount currencyCode }
      compareAtPrice { amount currencyCode }
      image { altText url }
      torsoOverlayValue: metafield(namespace: "custom", key: "torso_overlay") { value }
      skintoneValue: metafield(namespace: "custom", key: "skin_tone") { value }
      reverseImage: metafield(namespace: "custom", key: "reverse_image") { reference { ... on MediaImage { image { url } } } }
    `,
  })}
`;

export { VARIANT_SUPERSET_FIELDS as VARIANT_FIELDS_SUPERSET };
